import {
  Box,
  BoxProps,
  Button,
  CloseButton,
  Flex,
  Icon,
  Image,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";

import { v4 as uuid } from "uuid";
import { SiProducthunt } from "react-icons/si";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserDataHeader } from "@components/Header/UserDataHeader";

import { MenuItems } from "./MenuItems";
import { NavItem } from "./NavItem";
import { LogoutNavButton } from "./LogoutNavButton";
import { useAuth } from "@context/AuthContext";

import logoWhite from "@assets/images/logo_white.png";
import logoDark from "@assets/images/logo_black.png";
import logoMiniWhite from "@assets/images/logo192.png";
import logoMiniBlack from "@assets/images/logo192_black.png";
import { UserStore } from "@components/Header/UserStore";

interface Props extends BoxProps {
  onClose: () => void;
  isMenuCollapsed: boolean;
}

export const SidebarContent = ({
  onClose,
  isMenuCollapsed,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const { isUserAdmin, authState } = useAuth();
  const navigate = useNavigate();

  const widthMenu = isMenuCollapsed ? "95px" : "250px";
  const bgBox = useColorModeValue("white", "gray.700");
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleCreateNewProduct = () => {
    const productId = uuid();
    navigate(`/app/products/create/${productId}`);
    if (isMobile) setTimeout(onClose, 300);
  };

  const userSubName = authState.user?.subscription.subName || "";
  const clientName = authState.user?.subscription.subClientName || "";
  const menuItemsToRender = formatMenuItems(
    isUserAdmin,
    userSubName,
    clientName
  );

  const logoFullByTheme = useColorModeValue(logoDark, logoWhite);
  const logoMiniByTheme = useColorModeValue(logoMiniBlack, logoMiniWhite);
  const logoSrc = isMenuCollapsed ? logoMiniByTheme : logoFullByTheme;

  const handleCloseMenuOnClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    pageName: string
  ) => {
    if (isMobile) setTimeout(onClose, 300);
    if (pageName === "community") {
      e.preventDefault();
      window.open("https://www.skool.com/viraly", "_blank");
    }
  };

  return (
    <Box
      {...rest}
      h="100vh"
      overflowX="hidden"
      pos="fixed"
      transition="width .3s ease"
      w={{ base: "100%", md: widthMenu }}
      bg={bgBox}
    >
      <Box>
        <Flex justifyContent="center" mb={4} pt={4}>
          <Image
            width={isMenuCollapsed ? "30px" : "120px"}
            height={isMenuCollapsed ? "30px" : "auto"}
            mx={{ base: 0, lg: "auto" }}
            src={logoSrc}
          />
          <CloseButton
            display={{ base: "flex", md: "none" }}
            onClick={onClose}
            position="absolute"
            top={1}
            right={1}
          />
        </Flex>
        <UserDataHeader isMenuCollapsed={isMenuCollapsed} />
        {isMobile && <UserStore />}
        {isUserAdmin && !isMenuCollapsed && (
          <Box px={3} pt={2} pb={3}>
            <Button
              isFullWidth
              colorScheme="brand"
              leftIcon={<Icon as={SiProducthunt} />}
              onClick={handleCreateNewProduct}
              size="sm"
            >
              {t("productsAdminButtonNewProductText")}
            </Button>
          </Box>
        )}
      </Box>
      <Box overflowY="auto">
        {menuItemsToRender.map((link) => {
          const isStaging = process.env.REACT_APP_ENVIRONMENT !== "prod";
          const showColorTags = isStaging || isUserAdmin;
          if (link.disabled && !showColorTags) return null;

          // Extra gate: landing_creator is staging-only right now (route is also
          // gated in DashboardNavigation). Hide it in prod for everyone including
          // admins so they don't end up on a blank page.
          if (link.name === "landing_creator" && !isStaging) return null;
          if (link.name === "landings_ai" && !isStaging) return null;

          const tagColors = {
            "live": { bg: "green.50", border: "green.300" },
            "disabled": { bg: "red.50", border: "red.300" },
            "admin-only": { bg: "blue.50", border: "blue.300" },
          };
          const stagingStyle = showColorTags && link.stagingTag ? tagColors[link.stagingTag] : null;

          return (
            <Box
              key={link.name}
              bg={stagingStyle ? stagingStyle.bg : "transparent"}
              border={stagingStyle ? "2px solid" : "none"}
              borderColor={stagingStyle ? stagingStyle.border : "transparent"}
              borderRadius={stagingStyle ? 6 : 0}
              mx={stagingStyle ? 2 : 0}
              my={stagingStyle ? 1 : 0}
            >
              <NavItem
                isMenuCollapsed={isMenuCollapsed}
                icon={link.Icon}
                path={link.path}
                onClick={(e) => handleCloseMenuOnClick(e, link.name)}
              >
                {`${t(link.i18Key)}`}
              </NavItem>
            </Box>
          );
        })}
        {!isMobile && (
          <LogoutNavButton isMenuCollapsed={isMenuCollapsed} width="100%" />
        )}
      </Box>
    </Box>
  );
};

const formatMenuItems = (
  isAdmin: boolean,
  sub: string,
  subClientName: string
) => {
  console.log("SUB: ----------------------------------------> ", sub);
  if (isAdmin) {
    const itemsToShow = MenuItems.filter((item) => item.showToAdmin);
    return itemsToShow;
  } else {
    const noAdminItems = MenuItems.filter((item) => !item.isAdmin);
    // Case-insensitive match: some users in DB have clientName stored as
    // "free" (lowercase) while subsCanAccess lists "Free" with capital.
    const subLower = (sub || "").toLowerCase();
    const subClientLower = (subClientName || "").toLowerCase();
    const filteredBySub = noAdminItems.filter((item) => {
      const allowed = item.subsCanAccess.map((s) => s.toLowerCase());
      if (allowed.includes("all")) return true;
      if (allowed.includes(subLower)) return true;
      if (allowed.includes(subClientLower)) return true;
      return false;
    });

    const itemsToShow = filteredBySub;

    return itemsToShow;
  }
};
